import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { schema } from './crawlerFormSchema';
import CrawlStep1 from './CrawlStep1';
import CrawlStep2 from './CrawlStep2';
import CrawlStep3 from './CrawlStep3';
import CrawlStep4 from './CrawlStep4';
import CrawlStep5 from './CrawlStep5';
import CrawlStep6 from './CrawlStep6';
import { useUser } from '../../../../contexts/UserContext';
import ActionButton from '../../../elements/action-button/ActionButton';
import Modal from '../../../sections/modal/Modal';
import { stepperDefaultValues } from '../crawlerUtils';

const CrawlerStepperButton = ({
  getCollections,
  getFreeJobs,
  getMembers,
  getPayment,
  getUniqueKey,
  instances,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const { user } = useUser();

  const [step, setStep] = useState(0);
  const [modal, setModal] = useState(false);
  const [payment, setPayment] = useState({
    billingInfo: null,
    cards: null,
    managers: null,
  });

  const toggleModal = () => setModal(!modal);

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: stepperDefaultValues,
    shouldUnregister: false,
    resolver: yupResolver(schema()),
  });

  useEffect(() => {
    const handleGetPayment = async () => {
      const paymentInfo = await getPayment();
      setPayment(paymentInfo);
    };
    handleGetPayment();
  }, []);

  useEffect(() => {
    if (modal) return;

    formMethods.reset();
    setStep(0);
  }, [modal]);

  const validationFields = {
    0: ['jobType'],
    1: ['jobName', 'user', 'seeds', 'subseeds'],
    2: ['includeInCrawl', 'includeInIndex'],
    3: [
      'instance',
      'collection',
      'uniqueKey',
      'updateHandler',
      'removeHandler',
      'statusHandler',
      'commitTime',
      'commitAfterJob',
      'crawlArgs',
    ],
    4: ['periodType', 'periodTime', 'date', 'day'],
  };

  const nextStep = async () => {
    const fieldsToValidate = validationFields[step];
    const isValid = await formMethods.trigger(fieldsToValidate);
    if (isValid) setStep(step + 1);
  };
  const prevStep = () => setStep(step - 1);

  const isFirstStep = step === 0;
  const isLastStep = step === 5;

  const stepTitles = [
    'crawler.typeSettings',
    'crawler.jobSettings',
    'crawler.extraSettings',
    'crawler.solrSettings',
    'crawler.scheduleSettings',
    'crawler.confirm',
  ];

  const handleSubmit = async (formData) => {
    await onSubmit(formData);
    toggleModal();
  };

  return (
    user && (
      <>
        {user.isAdmin(projectId) && (
          <Button
            onClick={toggleModal}
            aria-label="add-crawler-job"
            startIcon={<AddIcon />}
          >
            {t('addCrawlerJob', { ns: 'buttons' })}
          </Button>
        )}
        <Modal
          title={t('crawler.jobSetup', { ns: 'sectionTitles' })}
          openModal={modal}
          handleClose={toggleModal}
          maxWidth="md"
        >
          <Stepper activeStep={step} alternativeLabel sx={{ mt: -2 }}>
            {stepTitles.map((step) => (
              <Step key={step}>
                <StepLabel>{t(step, { ns: 'sectionTitles' })}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <FormProvider {...formMethods}>
            <form autoComplete="off">
              <Box>
                {step === 0 && (
                  <CrawlStep1 getFreeJobs={getFreeJobs} payment={payment} />
                )}
                {step === 1 && <CrawlStep2 getMembers={getMembers} />}
                {step === 2 && <CrawlStep3 />}
                {step === 3 && (
                  <CrawlStep4
                    getCollections={getCollections}
                    getUniqueKey={getUniqueKey}
                    instances={instances}
                  />
                )}
                {step === 4 && <CrawlStep5 />}
                {step === 5 && (
                  <CrawlStep6
                    card={payment.cards}
                    billingInfo={payment.billingInfo}
                    instances={instances}
                  />
                )}
              </Box>

              <Box
                sx={{ display: 'flex', mt: 2, justifyContent: 'space-between' }}
              >
                <Button
                  variant="text"
                  onClick={isFirstStep ? toggleModal : prevStep}
                >
                  {isFirstStep
                    ? t('cancel', { ns: 'buttons' })
                    : t('previousStep', { ns: 'buttons' })}
                </Button>
                {isLastStep ? (
                  <ActionButton
                    clickAction={formMethods.handleSubmit(handleSubmit)}
                  >
                    {t('createCrawlerJob', { ns: 'buttons' })}
                  </ActionButton>
                ) : (
                  <Button variant="text" onClick={nextStep}>
                    {t('nextStep', { ns: 'buttons' })}
                  </Button>
                )}
              </Box>
            </form>
          </FormProvider>
        </Modal>
      </>
    )
  );
};

CrawlerStepperButton.propTypes = {
  getCollections: PropTypes.func.isRequired,
  getFreeJobs: PropTypes.func.isRequired,
  getMembers: PropTypes.func.isRequired,
  getPayment: PropTypes.func.isRequired,
  getUniqueKey: PropTypes.func.isRequired,
  instances: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default CrawlerStepperButton;
